import { create } from 'zustand';
import { ProjectSimpleData, ProjectData } from '../dto/EntityRepository';
import { ProjectListType } from '../enum/EnumRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

interface ProjectListState {
  projects: ProjectSimpleData[];
  projectListType: ProjectListType | null;
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
  setProjects: (projects: ProjectSimpleData[]) => void;
  afterCreateProject: (project: ProjectSimpleData) => void;
  setProjectListType: (projectListType: ProjectListType) => void;
  beginProjectListFetch: (ownerAui: string, scope: ResourceScope) => void;
  setProjectListAndType: (
    projects: ProjectSimpleData[],
    projectListType: ProjectListType,
    ownerAui: string,
    scope: ResourceScope,
  ) => void;
  setProjectListError: () => void;
  setProjectAccessAfterUpdate: (project: ProjectData) => void;
  afterDeleteProject: (id: string) => void;
  clearProjectList: () => void;
}

const isSameProjectList = (
  prev: ProjectSimpleData[],
  next: ProjectSimpleData[],
): boolean =>
  prev.length === next.length &&
  prev.every((project, index) => {
    const other = next[index];
    return (
      project.id === other.id &&
      project.title === other.title &&
      project.originUrl === other.originUrl &&
      project.focusX === other.focusX &&
      project.focusY === other.focusY &&
      project.isPublic === other.isPublic &&
      project.projectType === other.projectType &&
      project.projectYear === other.projectYear
    );
  });

export const useProjectListStore = create<ProjectListState>((set) => ({
  projects: [],
  projectListType: null,
  listOwnerAui: null,
  listScope: null,
  status: 'idle',
  setProjects: (projects) => set({ projects }),
  afterCreateProject: (project) => set((state) => ({
    projects: [...state.projects, project]
  })),
  setProjectListType: (projectListType) => set({ projectListType }),
  beginProjectListFetch: (ownerAui, scope) => set((state) => {
    // 같은 owner+scope 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setProjectListAndType: (projects, projectListType, ownerAui, scope) =>
    set((state) => {
      if (
        state.status === 'success' &&
        state.listOwnerAui === ownerAui &&
        state.listScope === scope &&
        state.projectListType === projectListType &&
        isSameProjectList(state.projects, projects)
      ) {
        return state;
      }
      return {
        projects,
        projectListType,
        listOwnerAui: ownerAui,
        listScope: scope,
        status: 'success',
      };
    }),
  setProjectListError: () => set({ status: 'error' }),
  setProjectAccessAfterUpdate: (updatedProject) => set((state) => ({
    projects: state.projects.map((project) =>
      project.id === updatedProject.id
        ? { ...project, isPublic: updatedProject.isPublic }
        : project
    )
  })),
  afterDeleteProject: (id) => set((state) => ({
    projects: state.projects.filter((project) => project.id !== id)
  })),
  clearProjectList: () => set((state) => {
    if (
      state.projects.length === 0 &&
      state.projectListType === null &&
      state.listOwnerAui === null &&
      state.listScope === null &&
      state.status === 'idle'
    ) {
      return state;
    }
    return {
      projects: [],
      projectListType: null,
      listOwnerAui: null,
      listScope: null,
      status: 'idle',
    };
  }),
}));
